import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import clientService, {
  ClientImportResponse,
  ClientRecord,
  ClientUpdatePayload,
} from '../../services/clientService';
import { VOICE_MODEL_OPTIONS } from '../../constants/clientVoice';

type EditFormState = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  origen: string;
  voice_model_selection: string;
  customer_timezone: string;
  hubspot_sync_status: string;
  data_policy_accepted: boolean;
};

const SYNC_STATUS_LABELS: Record<string, string> = {
  synced: 'Sincronizado',
  pending_create: 'Pendiente CRM',
  pending_recheck: 'Pendiente Revisión',
  crm_mismatch: 'Identidad Distinta',
  sync_error: 'Error de Sincronización',
};

const syncStatusChipColor = (status: string) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'synced') return 'success';
  if (normalized === 'sync_error') return 'error';
  if (normalized === 'crm_mismatch') return 'warning';
  return 'default';
};

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-CO');
};

const profilePreview = (record: ClientRecord): string => {
  const attrs = record.profile_attributes || {};
  const pairs = Object.entries(attrs)
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${value}`);
  return pairs.length > 0 ? pairs.join(' | ') : '-';
};

const voiceModelLabel = (value?: string | null): string => {
  const cleaned = String(value || '').trim();
  return cleaned || 'Predeterminada';
};

const Clients: React.FC = () => {
  const [rows, setRows] = useState<ClientRecord[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [importSummary, setImportSummary] = useState<ClientImportResponse | null>(null);

  const [editingRow, setEditingRow] = useState<EditFormState | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingRow, setDeletingRow] = useState<ClientRecord | null>(null);
  const [deletingClient, setDeletingClient] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importOrigen, setImportOrigen] = useState('');
  const [importPolicyPreaccepted, setImportPolicyPreaccepted] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await clientService.listClients({
        skip: page * pageSize,
        limit: pageSize,
        search: search.trim() || undefined,
      });
      setRows(response.clients || []);
      setTotalRows(response.total_count || 0);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible cargar los contactos.';
      setErrorMessage(String(backendMessage));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleDownloadTemplate = async () => {
    setErrorMessage(null);
    try {
      const blob = await clientService.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'clients_template.xlsx';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible descargar la plantilla.';
      setErrorMessage(String(backendMessage));
    }
  };

  const resetImportDialogState = () => {
    setImportOrigen('');
    setImportPolicyPreaccepted(false);
    setImportFile(null);
  };

  const closeImportDialog = () => {
    if (importing) return;
    setImportDialogOpen(false);
    resetImportDialogState();
  };

  const handleSelectImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImportFile(file);
    }
    event.target.value = '';
  };

  const handleProcessImport = async () => {
    if (!importFile) {
      setErrorMessage('Debes seleccionar un archivo .xlsx para continuar.');
      return;
    }

    const originTrimmed = importOrigen.trim();
    if (!originTrimmed) {
      setErrorMessage('Debes ingresar el Origen de la lista antes de procesar.');
      return;
    }

    setImporting(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    setImportSummary(null);
    try {
      const result = await clientService.importClients(
        importFile,
        originTrimmed,
        importPolicyPreaccepted
      );
      setImportSummary(result);
      setSuccessMessage(
        `Importación finalizada. Creados: ${result.created_rows}, Actualizados: ${result.updated_rows}, Sin cambios: ${result.unchanged_rows}, Inválidos: ${result.invalid_rows}.`
      );
      setImportDialogOpen(false);
      resetImportDialogState();
      await fetchRows();
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible importar el archivo.';
      setErrorMessage(String(backendMessage));
    } finally {
      setImporting(false);
    }
  };

  const handleOpenEdit = (row: ClientRecord) => {
    setEditingRow({
      id: row.id,
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      email: row.email || '',
      origen: row.origen || '',
      voice_model_selection: row.voice_model_selection || '',
      customer_timezone: row.customer_timezone || '',
      hubspot_sync_status: row.hubspot_sync_status || 'pending_create',
      data_policy_accepted: Boolean(row.data_policy_accepted),
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRow) return;
    setSavingEdit(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const payload: ClientUpdatePayload = {
        first_name: editingRow.first_name || null,
        last_name: editingRow.last_name || null,
        email: editingRow.email || null,
        voice_model_selection: editingRow.voice_model_selection,
        customer_timezone: editingRow.customer_timezone || null,
        hubspot_sync_status: editingRow.hubspot_sync_status || null,
        data_policy_accepted: editingRow.data_policy_accepted,
      };
      await clientService.updateClient(editingRow.id, payload);
      setEditingRow(null);
      setSuccessMessage('Contacto actualizado correctamente.');
      await fetchRows();
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible guardar el contacto.';
      setErrorMessage(String(backendMessage));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!deletingRow) return;
    setDeletingClient(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      await clientService.deleteClient(deletingRow.id);
      setSuccessMessage('Contacto eliminado correctamente de la base local.');
      setDeletingRow(null);
      await fetchRows();
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible eliminar el contacto.';
      setErrorMessage(String(backendMessage));
    } finally {
      setDeletingClient(false);
    }
  };

  const columns = useMemo<GridColDef<ClientRecord>[]>(
    () => [
      {
        field: 'phone_number',
        headerName: 'Teléfono',
        flex: 1,
        minWidth: 140,
      },
      {
        field: 'first_name',
        headerName: 'Nombre',
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => params.row.first_name || '-',
      },
      {
        field: 'last_name',
        headerName: 'Apellido',
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => params.row.last_name || '-',
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.3,
        minWidth: 180,
        valueGetter: (params) => params.row.email || '-',
      },
      {
        field: 'origen',
        headerName: 'Origen',
        minWidth: 220,
        valueGetter: (params) => params.row.origen || '-',
      },
      {
        field: 'voice_model_selection',
        headerName: 'Voz TTS',
        minWidth: 150,
        valueGetter: (params) => voiceModelLabel(params.row.voice_model_selection),
      },
      {
        field: 'customer_timezone',
        headerName: 'Zona horaria',
        minWidth: 180,
        valueGetter: (params) => params.row.customer_timezone || 'Inferida',
      },
      {
        field: 'hubspot_contact_id',
        headerName: 'HubSpot Contact ID',
        minWidth: 210,
        valueGetter: (params) => params.row.hubspot_contact_id || '-',
      },
      {
        field: 'hubspot_sync_status',
        headerName: 'Estado CRM',
        minWidth: 180,
        renderCell: (params) => {
          const raw = String(params.row.hubspot_sync_status || '').toLowerCase();
          const label = SYNC_STATUS_LABELS[raw] || params.row.hubspot_sync_status || '-';
          return <Chip size="small" color={syncStatusChipColor(raw)} label={label} />;
        },
      },
      {
        field: 'data_policy_accepted',
        headerName: 'Política',
        minWidth: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            color={params.row.data_policy_accepted ? 'success' : 'default'}
            label={params.row.data_policy_accepted ? 'Aceptada' : 'Pendiente'}
          />
        ),
      },
      {
        field: 'profile_attributes',
        headerName: 'Atributos Perfil',
        flex: 1.8,
        minWidth: 260,
        valueGetter: (params) => profilePreview(params.row),
      },
      {
        field: 'last_seen_at',
        headerName: 'Último Contacto',
        minWidth: 180,
        valueGetter: (params) => formatDate(params.row.last_seen_at),
      },
      {
        field: 'actions',
        headerName: 'Acciones',
        minWidth: 250,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleOpenEdit(params.row)}
            >
              Editar
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setDeletingRow(params.row)}
            >
              Eliminar
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
        Contactos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 980 }}>
        Esta vista centraliza la información de contactos en nuestra base local para asegurar continuidad comercial,
        incluso cuando aún no existe contacto en CRM. Si ya cuentas con datos previos, súbelos antes de iniciar una
        campaña; sincronizaremos con HubSpot únicamente cuando el contacto avance en el embudo de adquisición.
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          label="Buscar"
          size="small"
          value={search}
          onChange={(event) => {
            setPage(0);
            setSearch(event.target.value);
          }}
        />
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
          Descargar Plantilla
        </Button>
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={() => setImportDialogOpen(true)}
        >
          Subir Contactos
        </Button>
      </Stack>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {importSummary && importSummary.invalid_rows > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Se detectaron {importSummary.invalid_rows} fila(s) inválida(s) durante la importación.
        </Alert>
      )}

      <Paper sx={{ height: 650, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          pageSize={pageSize}
          onPageSizeChange={(newSize) => {
            setPage(0);
            setPageSize(newSize);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Paper>

      <Dialog
        open={importDialogOpen}
        onClose={closeImportDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Configurar Carga de Contactos</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Origen"
              placeholder="Ejemplo: Campaña Feria Medellín 2026"
              value={importOrigen}
              onChange={(event) => setImportOrigen(event.target.value)}
              required
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                Los contactos de esta lista ya aceptaron la política de datos
              </Typography>
              <Switch
                checked={importPolicyPreaccepted}
                onChange={(event) => setImportPolicyPreaccepted(event.target.checked)}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                Subir Archivo
                <input hidden type="file" accept=".xlsx" onChange={handleSelectImportFile} />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {importFile?.name || 'Ningún archivo seleccionado'}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              La columna opcional <code>voice_model_selection</code> acepta <code>Antioquia</code>, <code>Caribe</code>, <code>Bogota</code>, <code>Republica Dominicana</code> o vacío.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImportDialog} disabled={importing}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleProcessImport}
            disabled={importing || !importFile || !importOrigen.trim()}
          >
            {importing ? 'Procesando...' : 'Procesar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingRow)} onClose={() => setEditingRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>Editar Contacto</DialogTitle>
        <DialogContent>
          {!editingRow ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Nombre"
                value={editingRow.first_name}
                onChange={(event) =>
                  setEditingRow((prev) =>
                    prev ? { ...prev, first_name: event.target.value } : prev
                  )
                }
              />
              <TextField
                label="Apellido"
                value={editingRow.last_name}
                onChange={(event) =>
                  setEditingRow((prev) =>
                    prev ? { ...prev, last_name: event.target.value } : prev
                  )
                }
              />
              <TextField
                label="Email"
                value={editingRow.email}
                onChange={(event) =>
                  setEditingRow((prev) =>
                    prev ? { ...prev, email: event.target.value } : prev
                  )
                }
              />
              <TextField
                label="Origen"
                value={editingRow.origen}
                InputProps={{ readOnly: true }}
                helperText="Este campo es informativo y no editable."
              />
              <TextField
                select
                label="Voz TTS"
                value={editingRow.voice_model_selection}
                onChange={(event) =>
                  setEditingRow((prev) =>
                    prev ? { ...prev, voice_model_selection: event.target.value } : prev
                  )
                }
                helperText="Predeterminada no envía un modelo al API principal."
              >
                {VOICE_MODEL_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'default'} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Zona horaria cliente"
                value={editingRow.customer_timezone}
                onChange={(event) =>
                  setEditingRow((prev) =>
                    prev ? { ...prev, customer_timezone: event.target.value } : prev
                  )
                }
                helperText="IANA, ej: America/Santo_Domingo. Vacío = inferir por teléfono."
              />
              <TextField
                label="Estado CRM"
                value={editingRow.hubspot_sync_status}
                onChange={(event) =>
                  setEditingRow((prev) =>
                    prev ? { ...prev, hubspot_sync_status: event.target.value } : prev
                  )
                }
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Política de Datos Aceptada</Typography>
                <Switch
                  checked={editingRow.data_policy_accepted}
                  onChange={(event) =>
                    setEditingRow((prev) =>
                      prev ? { ...prev, data_policy_accepted: event.target.checked } : prev
                    )
                  }
                />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingRow(null)} disabled={savingEdit}>
            Cancelar
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={savingEdit || !editingRow}>
            {savingEdit ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingRow)}
        onClose={() => (deletingClient ? undefined : setDeletingRow(null))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Eliminar Contacto</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Esta acción eliminará el registro únicamente de la base local. No realizará eliminación en HubSpot.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {`¿Deseas eliminar el contacto ${deletingRow?.phone_number || ''}?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingRow(null)} disabled={deletingClient}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteClient}
            disabled={deletingClient || !deletingRow}
          >
            {deletingClient ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clients;
